const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  addToSet: '[data-product-add-to-set]',
  addToSetText: '[data-product-add-to-set-text]',
  buyNow: '[data-product-buy-now]',
  media: '[data-product-media]',
  price: '[data-product-price]',
  backInStock: '[data-product-bis]',
  forms: '[data-product-forms]',
}

export class ProductInfo extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form']
  }

  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  abortController = undefined
  pendingRequestUrl = undefined
  preReplaceProductOptionsCallbacks = []
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()

    if (this.els.form.element.tagName !== 'PRODUCT-FORM') {
      this.throwError(`this.els.form.element should be a <product-form>`)
    }
  }

  constructor () {
    super()
    this.shouldShowGlobalLoader = false
  }

  setupListeners () {
    super.setupListeners()

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    this.postReplacePageCallbacks.push(() => {
      window.yotpoWidgetsContainer?.initWidgets()
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {string[]} payload.detail.selectedOptionValues
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId

      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {HTMLOptionElement | EventTarget} payload.detail.target
   * @param {string[]} payload.detail.selectedOptionValues
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    const productInfo = target?.closest?.('product-info')
    if (!productInfo) return
    if (productInfo.dataset.productInfoId !== this.dataset.productInfoId) {
      return
    }

    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    this.shouldShowGlobalLoader = shouldFetchFullPage
    this.loading = true

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   */

  /**
   * @param {Object} payload
   * @param {string} payload.requestUrl
   * @param {string} payload.targetId
   * @param {fetchProductDetailsCallback} payload.callback
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const text = await fetchProductData(
        requestUrl,
        this.abortController.signal,
      )

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      window.BAO.Utils.getElement(`#${targetId}`)?.focus()
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   */
  handleSwapProduct (productUrl, updateFullPage = false) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      this.updateURL(productUrl, variant?.id)

      const clearLoadingCallback = () => {
        try {
          this.loading = false
        } catch (err) {
          console.warn('Could not clear loading state:', err)
        }
      }

      const postReplaceCallbacks = [
        ...this.postReplacePageCallbacks,
        clearLoadingCallback,
      ]

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        window.BAO.Utils.gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          postReplaceCallbacks,
        )
      } else {
        window.BAO.Utils.gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          postReplaceCallbacks,
        )
      }
    }
  }

  /**
   * @param {string} productUrl
   */
  renderProductDetails (productUrl) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (!variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)

        this.loading = false

        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant.id)
      this.updateVariantInputs(variant.id)

      /**
       * @callback PostReplaceCallback
       * @param {Element} currentElement
       * @param {Element} newElement
       */

      /**
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = window.BAO.Utils.getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.forms}`,
      )

      if (variant.available) {
        this.updateAvailability(true, window.theme.strings.addToCart)
      } else {
        this.updateAvailability(false, window.theme.strings.soldOut)
      }

      const addToSetEl = window.BAO.Utils.getElement(
        `[data-product-info-id="${this.productInfoId}"]${selectors.addToSet}`,
      )
      if (addToSetEl) {
        addToSetEl.dataset.variantId = variant.id
        addToSetEl.dataset.variantPrice = variant.price
      }

      window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        html,
        variant,
      })

      this.loading = false
    }
  }

  updateAvailability (productIsAvailable, text) {
    const addToCartEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )
    const addToCartTextEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartText}`,
    )
    const backInStockEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStock}`,
    )

    const addToSetEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToSet}`,
    )
    const addToSetTextEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToSetText}`,
    )

    const buyNowEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.buyNow}`,
    )

    addToCartEls.forEach((addToCartEl) => {
      addToCartEl.toggleAttribute('disabled', !productIsAvailable)
    })
    backInStockEls.forEach((backInStockEl) => {
      backInStockEl.setAttribute('aria-hidden', productIsAvailable)
    })

    addToCartTextEls.forEach((addToCartTextEl) => {
      addToCartTextEl.textContent =
        text ||
        (productIsAvailable
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut)
    })

    addToSetEls.forEach((addToSetEl) =>
      addToSetEl.toggleAttribute('disabled', !productIsAvailable),
    )

    addToSetTextEls.forEach((addToSetTextEl) => {
      addToSetTextEl.textContent = productIsAvailable
        ? window.theme.strings.buildASet.addToSet
        : window.theme.strings.soldOut
    })

    buyNowEls.forEach((buyNowEl) => {
      buyNowEl.toggleAttribute('disabled', !productIsAvailable)
      buyNowEl.toggleAttribute('aria-hidden', !productIsAvailable)
    })
  }

  /**
   * @param {Document} html
   */
  updateOptionValues (html) {
    const productOptions = html.querySelector(
      `${this.productOptions.tagName.toLowerCase()}`,
    )

    if (productOptions) {
      window.BAO.Utils.gracefullyReplaceNode(
        this.productOptions,
        productOptions,
        this.preReplaceProductOptionsCallbacks,
      )
    }
  }

  /**
   * @param {string} url
   * @param {string} [variantId]
   */
  updateURL (url, variantId) {
    const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`

    const addToSetEl = window.BAO.Utils.getElement(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToSet}`,
    )
    if (addToSetEl) {
      addToSetEl.dataset.variantUrl = newUrl
    }
    if (this.dataset.updateUrl === 'false') return

    window.history.replaceState({}, '', newUrl)

    const shareButtons = window.BAO.Utils.getElements('share-button')

    if (!shareButtons) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(newUrl)
    })
  }

  /**
   * @param {string} [variantId]
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    const input = this.productForm.querySelector('input[name="id"]')
    if (!input) return

    input.value = variantId
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /**
   * @param {string} url
   * @param {string[]} optionValues
   * @param {boolean} shouldFetchFullPage
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  /**
   * @param {Document} productInfoNode
   *
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    const selectedVariantDataEl = productInfoNode.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="selected-variant"]`,
    )
    if (!selectedVariantDataEl) return null

    return JSON.parse(selectedVariantDataEl.innerHTML)
  }

  get productForm () {
    return this.els.form.element
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els.options.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', String(val))
    if (!this.shouldShowGlobalLoader) return
    if (val) {
      window.loadingcount = (window.loadingcount || 0) + 1
      document.body.classList.add('util-ProductsLoading')
    } else {
      setTimeout(() => {
        window.loadingcount = Math.max(0, (window.loadingcount || 0) - 1)
        if (!window.loadingcount) {
          document.body.classList.remove('util-ProductsLoading')
        }
      }, 400)
    }
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * @param {string} requestUrl
 * @param {AbortSignal} abortSignal
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
